import { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import AiPlanActiveSwitch from "../AiPlanActiveSwitch";
import {
  DataGridPremium,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  aiPlanDataGridPanelSx,
  getDataGridEmptyStateSx,
  getRegulationsListDataGridSx,
} from "../../../components/shared/regulationsListDataGridSx";
import styles from "../../../components/RegulationsTable/RegulationsTable.module.css";
import noDataAnim from "../../../assets/no-data-anim.webp";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import {
  AUTO_TAGGING_ACTIVATE_SUCCESS_TOAST,
  AUTO_TAGGING_DEACTIVATE_SUCCESS_TOAST,
  deleteAutoTaggingPlan,
  updateAutoTaggingPlanActive,
} from "../../../services/autoTaggingService";
import { AUTO_TAGGING_PRIVILEGES } from "../../../services/permissionsService";
import { CONCEPT_VALUE_ALL_OPTION, type TaggingPlanRow } from "../constants";
import { TruncatedTooltipCell } from "./TruncatedTooltipCell";
import { DataGridManageColumnsPanel } from "../../../components/shared/DataGridManageColumnsPanel";
import DeleteTaggingPlanConfirmDialog from "./DeleteTaggingPlanConfirmDialog";
import EditAssessmentPlanItemDialog from "./EditAssessmentPlanItemDialog";
import ReferenceItemsCell, {
  buildReferenceItemDetails,
  formatReferenceItemsRnLabel,
} from "./ReferenceItemsCell";
import TaggingPlanActiveConfirmDialog, {
  type TaggingPlanActiveConfirmMode,
} from "./TaggingPlanActiveConfirmDialog";
import TaggingPlanRowActions from "./TaggingPlanRowActions";
import TaggingPlanTableToolbar from "./TaggingPlanTableToolbar";

/** Homepage Impact item: `Topic = A, B` (or `Topic = All`). */
function formatImpactItemDisplay(row: TaggingPlanRow): string {
  const dimension = row.concept?.trim() || "";
  if (!dimension) {
    if (row.allValues) return CONCEPT_VALUE_ALL_OPTION.name;
    return row.values?.trim() || "";
  }
  if (row.allValues) return `${dimension} = ${CONCEPT_VALUE_ALL_OPTION.name}`;

  const valueNames =
    row.conceptValues?.length > 0
      ? row.conceptValues
          .map((item) => item.name?.trim() || item.code)
          .filter(Boolean)
      : (row.values?.trim() || "")
          .split(",")
          .map((part) => part.trim())
          .filter(Boolean);

  if (valueNames.length === 0) return dimension;
  return `${dimension} = ${valueNames.join(", ")}`;
}

type TaggingPlanTableProps = Readonly<{
  rows: TaggingPlanRow[];
  rowCount: number;
  loading?: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onAddNew?: () => void;
  onPlanDeleted?: () => void;
  onPlanUpdated?: () => void;
}>;

function TaggingPlanNoRowsOverlay({ onAddNew }: { onAddNew?: () => void }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        minHeight: 280,
        py: 4,
        boxSizing: "border-box",
        bgcolor: "background.paper",
      }}
    >
      <Box
        component="img"
        src={noDataAnim}
        alt=""
        sx={{ width: 160, height: "auto", mb: 2 }}
      />
      <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
        No data available.{" "}
        {onAddNew ? (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={onAddNew}
            sx={{
              color: "#295EF1",
              fontSize: "14px",
              fontWeight: 500,
              verticalAlign: "baseline",
              cursor: "pointer",
            }}
          >
            Add Now
          </Link>
        ) : null}
      </Typography>
    </Box>
  );
}

export default function TaggingPlanTable({
  rows,
  rowCount,
  loading = false,
  paginationModel,
  onPaginationModelChange,
  onAddNew,
  onPlanDeleted,
  onPlanUpdated,
}: TaggingPlanTableProps) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const { showMessage } = useSnackbar();
  const canManageAutoTagging = useHasPrivilege(AUTO_TAGGING_PRIVILEGES.manage);
  const [deleteTarget, setDeleteTarget] = useState<TaggingPlanRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editPlan, setEditPlan] = useState<TaggingPlanRow | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [activeToggleTarget, setActiveToggleTarget] = useState<{
    row: TaggingPlanRow;
    nextActive: boolean;
  } | null>(null);
  const [updatingActive, setUpdatingActive] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const manageColumnsPanelScrollTopRef = useRef(0);

  const columnsManagementSlot = useCallback(
    () => (
      <DataGridManageColumnsPanel
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        scrollTopRef={manageColumnsPanelScrollTopRef}
      />
    ),
    [columnVisibilityModel],
  );

  const noRowsOverlaySlot = useCallback(
    () => (
      <TaggingPlanNoRowsOverlay
        onAddNew={canManageAutoTagging ? onAddNew : undefined}
      />
    ),
    [canManageAutoTagging, onAddNew],
  );

  const handleDeleteRequest = useCallback((row: TaggingPlanRow) => {
    setDeleteTarget(row);
  }, []);

  const handleEditRequest = useCallback((row: TaggingPlanRow) => {
    setEditPlan(row);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    if (editSaving) return;
    setEditPlan(null);
  }, [editSaving]);

  const handleCloseDeleteDialog = useCallback(() => {
    if (deleting) return;
    setDeleteTarget(null);
  }, [deleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget || deleting) return;

    setDeleting(true);
    try {
      await deleteAutoTaggingPlan(deleteTarget.id);
      showMessage("Classification deleted successfully.", "success");
      setDeleteTarget(null);
      onPlanDeleted?.();
    } catch (error) {
      showMessage(
        error instanceof Error ? error.message : "Failed to delete Classification.",
        "error",
      );
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleting, onPlanDeleted, showMessage]);

  const activeConfirmMode: TaggingPlanActiveConfirmMode | null = activeToggleTarget
    ? activeToggleTarget.nextActive
      ? "activate"
      : "deactivate"
    : null;

  const handleActiveToggleRequest = useCallback(
    (row: TaggingPlanRow, nextActive: boolean) => {
      setActiveToggleTarget({ row, nextActive });
    },
    [],
  );

  const handleCloseActiveDialog = useCallback(() => {
    if (updatingActive) return;
    setActiveToggleTarget(null);
  }, [updatingActive]);

  const handleConfirmActiveToggle = useCallback(async () => {
    if (!activeToggleTarget || updatingActive) return;

    const { row, nextActive } = activeToggleTarget;
    setUpdatingActive(true);
    try {
      await updateAutoTaggingPlanActive(row.id, nextActive);
      showMessage(
        nextActive
          ? AUTO_TAGGING_ACTIVATE_SUCCESS_TOAST
          : AUTO_TAGGING_DEACTIVATE_SUCCESS_TOAST,
        "success",
      );
      setActiveToggleTarget(null);
      onPlanUpdated?.();
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : "Failed to update Classification status.",
        "error",
      );
    } finally {
      setUpdatingActive(false);
    }
  }, [activeToggleTarget, onPlanUpdated, showMessage, updatingActive]);

  const columns = useMemo<GridColDef<TaggingPlanRow>[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 100,
        sortable: false,
        filterable: false,
      },
      {
        field: "name",
        headerName: "Assessment Name",
        flex: 0.5,
        minWidth: 110,
        sortable: false,
        filterable: false,
        cellClassName: "ai-plan-description-cell",
        valueGetter: (_value, row) =>
          row.name?.trim() || row.description?.trim() || "",
        renderCell: ({ value }) => {
          const text = typeof value === "string" ? value.trim() : "";
          if (!text) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
                  -
                </Typography>
              </Box>
            );
          }
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                minWidth: 0,
              }}
            >
              <TruncatedTooltipCell text={text} />
            </Box>
          );
        },
      },
      {
        field: "impactItem",
        headerName: "Impact item",
        flex: 1,
        minWidth: 200,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => formatImpactItemDisplay(row),
        renderCell: ({ row }) => {
          const text = formatImpactItemDisplay(row);
          if (!text) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
                  -
                </Typography>
              </Box>
            );
          }
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                minWidth: 0,
              }}
            >
              <TruncatedTooltipCell text={text} />
            </Box>
          );
        },
      },
      {
        field: "referenceItems",
        headerName: "Reference Items",
        width: 160,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) =>
          formatReferenceItemsRnLabel(buildReferenceItemDetails(row)),
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              minWidth: 0,
            }}
          >
            <ReferenceItemsCell row={row} />
          </Box>
        ),
      },
      {
        field: "assessmentTemplate",
        headerName: "Assessment Template",
        width: 160,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.assessmentTemplate?.trim() || "",
        renderCell: ({ value }) => {
          const text = typeof value === "string" ? value.trim() : "";
          if (!text) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
                  -
                </Typography>
              </Box>
            );
          }
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                minWidth: 0,
              }}
            >
              <TruncatedTooltipCell text={text} />
            </Box>
          );
        },
      },
      {
        field: "enabled",
        headerName: "Active",
        width: 88,
        sortable: false,
        filterable: false,
        resizable: false,
        disableExport: true,
        align: "center",
        headerAlign: "center",
        cellClassName: "ai-plan-status-cell",
        renderCell: ({ row }) => (
          <AiPlanActiveSwitch
            checked={row.enabled}
            disabled={!canManageAutoTagging}
            onChange={(nextChecked) =>
              handleActiveToggleRequest(row, nextChecked)
            }
          />
        ),
      },
      ...(canManageAutoTagging
        ? [
            {
              field: "actions",
              headerName: "Actions",
              width: 88,
              sortable: false,
              filterable: false,
              resizable: false,
              disableExport: true,
              align: "center" as const,
              headerAlign: "center" as const,
              cellClassName: "ai-plan-actions-cell",
              renderCell: ({ row }: { row: TaggingPlanRow }) => (
                <TaggingPlanRowActions
                  onEdit={() => handleEditRequest(row)}
                  onDelete={() => handleDeleteRequest(row)}
                />
              ),
            } satisfies GridColDef<TaggingPlanRow>,
          ]
        : []),
    ],
    [
      canManageAutoTagging,
      handleActiveToggleRequest,
      handleDeleteRequest,
      handleEditRequest,
    ],
  );

  const isEmpty = rows.length === 0;

  return (
    <>
      <Box
      className={`${styles.dataGridPanel}${
        isEmpty ? ` ${styles.dataGridPanelEmpty}` : ""
      }`}
      sx={{
        ...aiPlanDataGridPanelSx,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 480,
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: "#fff",
        px: "16px",
        pt: "4px",
        pb: "4px",
        boxSizing: "border-box",
      }}
    >
      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        rowHeight={48}
        columnHeaderHeight={48}
        loading={loading}
        disableRowSelectionOnClick
        disableRowGrouping
        disableAggregation
        disablePivoting
        disableColumnSorting
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[5, 10, 20, 50]}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        showToolbar
        showColumnVerticalBorder={false}
        disableColumnPinning={false}
        pinnedColumnsSectionSeparator="border"
        localeText={{
          paginationRowsPerPage: "Listing per page:",
        }}
        slots={{
          toolbar: TaggingPlanTableToolbar,
          noRowsOverlay: noRowsOverlaySlot,
          columnsManagement: columnsManagementSlot,
        }}
        slotProps={{
          toolbar: {},
        }}
        initialState={{
          pinnedColumns: {
            right: canManageAutoTagging ? ["enabled", "actions"] : ["enabled"],
          },
        }}
        sx={
          [
            getRegulationsListDataGridSx(primaryColor),
            ...(isEmpty ? [getDataGridEmptyStateSx()] : []),
            {
              height: "100%",
              minHeight: 400,
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #E5E7EB",
                minHeight: 56,
              },
              "& .ai-plan-status-cell, & .ai-plan-actions-cell": {
                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "center !important",
                "& .MuiDataGrid-cellContent": {
                  display: "flex !important",
                  alignItems: "center !important",
                  justifyContent: "center !important",
                  width: "100%",
                  height: "100%",
                  overflow: "visible",
                },
              },
              "& .ai-plan-actions-cell.MuiDataGrid-cell--pinnedRight": {
                boxShadow: "none",
              },
              "& .ai-plan-status-cell.MuiDataGrid-cell--pinnedRight": {
                boxShadow: "none",
              },
            },
          ] as SxProps<Theme>
        }
      />
      </Box>
      <DeleteTaggingPlanConfirmDialog
        open={deleteTarget !== null}
        deleting={deleting}
        onClose={handleCloseDeleteDialog}
        onConfirm={() => void handleConfirmDelete()}
      />
      <EditAssessmentPlanItemDialog
        open={editPlan !== null}
        plan={editPlan}
        saving={editSaving}
        onClose={handleCloseEditDialog}
        onSavingChange={setEditSaving}
        onSave={() => {
          setEditPlan(null);
          onPlanUpdated?.();
        }}
        onSuccess={(message) => showMessage(message, "success")}
        onError={(message) => showMessage(message, "error")}
      />
      <TaggingPlanActiveConfirmDialog
        open={activeToggleTarget !== null}
        mode={activeConfirmMode}
        saving={updatingActive}
        onClose={handleCloseActiveDialog}
        onConfirm={() => void handleConfirmActiveToggle()}
      />
    </>
  );
}
